import {
  Box,
  HStack,
  IconButton,
  Stack,
  Text,
  useToast,
} from "@chakra-ui/react";
import {
  CreateAccessCodeRequest,
  EventDetailAccessCodesSection,
  UpdateTicketTypeAccessCodeRequest,
} from "@common/interfaces/KYDEvent";
import { FaPlus } from "react-icons/fa";
import { AccessCodesTable } from "./AccessCodesTable";
import { KYDTicketTypeAccessCode } from "@common/interfaces/KYDTicket";
import { SectionProps } from "../sections/sectionsComponents";
import { EditModalType } from "../../../utils";
import EditAccessCodeModal from "./EditAccessCodeModal";
import { useCallback, useState } from "react";
import { useLoading } from "../../../Manager/hooks/form";
import actions from "../../../actions";
import { useDialog } from "../../../Common/Dialog";
import ShowIfHasPermission from "../../ShowIfHasPermission";

const AccessCodesSection = ({
  data = {
    access_codes: [],
    ticket_types: [],
    tiered_ticket_types: [],
    currency: undefined,
    id: undefined,
    link: undefined,
    short_link: undefined,
    status: undefined,
    timezone: undefined,
  },
  modalType,
  setModalType,
  permissions,
  isLoading,
  refetchEvent,
}: SectionProps<EventDetailAccessCodesSection["data"]>) => {
  const [editingAccessCode, setEditingAccessCode] =
    useState<KYDTicketTypeAccessCode | null>(null);
  const toast = useToast();
  const dialog = useDialog();
  const [loading, updateLoading] = useLoading({
    accesscode: false,
  });

  const onCreateAccessCode = useCallback(
    async (_data: KYDTicketTypeAccessCode) => {
      let _;
      updateLoading("accesscode", true);
      try {
        if (!data.id) {
          throw Error("NO event ID provided");
        }

        const request: CreateAccessCodeRequest = {
          code: _data.code,
          valid_from: _data.valid_from,
          valid_to: _data.valid_to,
          ticket_type_ids: _data.ticket_type_ids,
          status: _data.status,
        };

        _ = await actions.ADMIN.createAccessCode(data.id, request);
        await refetchEvent({ silent: true });
        setModalType(EditModalType.INACTIVE);
        toast({
          title: "Access Code Created",
          description: "Access code has been created",
          status: "success",
          duration: 5000,
          isClosable: true,
        });
      } catch (err) {
        dialog({ text: err.message });
      }
      updateLoading("accesscode", false);
      return _;
    },
    [updateLoading, refetchEvent, dialog, data]
  );

  const onUpdateAccessCode = useCallback(
    async (accessCode: KYDTicketTypeAccessCode) => {
      let _;
      updateLoading("accesscode", true);
      try {
        const request: UpdateTicketTypeAccessCodeRequest = {
          code: accessCode.code,
          ticket_type_ids: accessCode.ticket_type_ids,
          valid_from: accessCode.valid_from || undefined,
          valid_to: accessCode.valid_to || undefined,
          status: accessCode.status,
        };

        _ = await actions.ADMIN.updateAccessCode(
          data.id,
          accessCode.id,
          request
        );
        await refetchEvent({ silent: true });
        setModalType(EditModalType.INACTIVE);
        toast({
          title: "Access Code Updated",
          description: "Access code has been updated",
          status: "success",
          duration: 5000,
          isClosable: true,
        });
      } catch (err) {
        dialog({ text: err.message });
      }
      updateLoading("accesscode", false);
      return _;
    },
    [updateLoading, refetchEvent, dialog, data]
  );

  const onOpenEditModal = useCallback(
    (accessCode: KYDTicketTypeAccessCode) => {
      setEditingAccessCode(accessCode);
      setModalType(EditModalType.ACCESS_CODE);
    },
    [setEditingAccessCode, setModalType]
  );

  const onOpenModal = useCallback(() => {
    setModalType(EditModalType.ACCESS_CODE);
  }, [setModalType]);

  const hasPermission = (permissionToCheck) =>
    permissions.includes(permissionToCheck);

  if (!data) {
    return null;
  }

  return (
    <>
      <EditAccessCodeModal
        isOpen={modalType === EditModalType.ACCESS_CODE}
        data={data}
        timezone={data.timezone}
        link={data.short_link || data.link}
        loading={loading}
        onClose={() => {
          setEditingAccessCode(null);
          setModalType(EditModalType.INACTIVE);
        }}
        onSubmit={editingAccessCode ? onUpdateAccessCode : onCreateAccessCode}
        accessCode={editingAccessCode}
      />
      <Stack>
        <HStack justify={"space-between"} w="100%">
          <Text fontWeight={"bold"}>Access Codes</Text>
          <ShowIfHasPermission permissions={permissions} checkFor="write">
            <IconButton
              isDisabled={isLoading}
              variant={"outline"}
              aria-label="Add access code"
              icon={<FaPlus />}
              onClick={() => onOpenModal()}
            />
          </ShowIfHasPermission>
        </HStack>
        <Box
          py={4}
          overflowX={data.access_codes.length === 0 ? "inherit" : "scroll"}
        >
          {data.access_codes.length === 0 ? (
            <Text color="gray.500">No access codes created</Text>
          ) : null}
          {data.access_codes.length > 0 ? (
            <AccessCodesTable
              readOnly={!hasPermission("write")}
              timezone={data.timezone}
              onEdit={(accessCode) => {
                onOpenEditModal(accessCode);
              }}
              accessCodes={data.access_codes}
              loading={loading}
              isDisabled={isLoading}
            />
          ) : null}
        </Box>
      </Stack>
    </>
  );
};

export default AccessCodesSection;
